import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Deja Vu Nomad's own California Seller of Travel registration. This is the
// site operator's number and belongs in the global footer; on a partner's
// package the partner's own number is the seller number (see variant="package").
export const DVN_SELLER = {
  name: 'Deja Vu Nomad',
  address: '2969 Delamar Drive, Dublin, CA 94568',
  phone: '408-674-4504',
  referenceNumber: '2175517-40',
  referenceKind: 'cst',
  fundsProtection: 'trust_account',
  tcrfParticipant: true,
};

const smallSx = { fontSize: 11.5, lineHeight: 1.6, color: 'text.secondary' };

/** Two-line CST block for the global footer (every page). */
export function SellerFooterLines({ sx = {} }) {
  const { t } = useTranslation();
  return (
    <Box data-testid="seller-footer-lines" sx={sx}>
      <Typography sx={smallSx}>{t('sellerDisclosures.footerSellerLine')}</Typography>
      <Typography sx={smallSx}>{t('sellerDisclosures.footerNotApproved')}</Typography>
    </Box>
  );
}

// US sellers register by state — only California is "CST".
const US_REGION_LABEL_KEYS = {
  CA: 'sellerDisclosures.cstLabel',
  FL: 'sellerDisclosures.labelFl',
  HI: 'sellerDisclosures.labelHi',
  WA: 'sellerDisclosures.labelWa',
};

export function sellerReferenceLabel(t, seller) {
  const region = String(seller.referenceRegion || '').toUpperCase();
  if (US_REGION_LABEL_KEYS[region]) return t(US_REGION_LABEL_KEYS[region]);
  if (seller.referenceKind === 'cst') return t('sellerDisclosures.cstLabel');
  if (region) return t('sellerDisclosures.referenceUsOther');
  return t('sellerDisclosures.licenseLabel');
}

function soldByText(t, seller) {
  const parts = [seller.name, seller.address].filter(Boolean).join(', ');
  const withPhone = [parts, seller.phone].filter(Boolean).join(' · ');
  if (!seller.referenceNumber) return withPhone;
  return `${withPhone} · ${sellerReferenceLabel(t, seller)} ${seller.referenceNumber}`;
}

/**
 * Full "Seller disclosures" box for a package page / checkout.
 * seller: { name, address, phone, referenceNumber, referenceKind,
 *           fundsProtection ('trust_account'|'bond'|'none'|null),
 *           tcrfParticipant }
 * buyerInCalifornia: true → covered TCRF text, false → not-covered line,
 *           null (package page) → participant status text.
 */
export function SellerDisclosureBox({ seller, buyerInCalifornia = null, operatedBy = null, sx = {} }) {
  const { t } = useTranslation();
  if (!seller || !seller.name) return null;

  const fundsLine = seller.fundsProtection === 'trust_account'
    ? t('sellerDisclosures.trustAccount')
    : seller.fundsProtection === 'bond'
      ? t('sellerDisclosures.bond')
      : null;

  let tcrfLine;
  if (!seller.tcrfParticipant) {
    tcrfLine = t('sellerDisclosures.tcrfNotParticipant');
  } else if (buyerInCalifornia === false) {
    tcrfLine = t('sellerDisclosures.tcrfNotCovered');
  } else {
    tcrfLine = t('sellerDisclosures.tcrfCovered');
  }

  return (
    <Box
      data-testid="seller-disclosures"
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ...sx,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {t('sellerDisclosures.title')}
      </Typography>
      <Typography sx={smallSx}>
        {t('sellerDisclosures.soldBy', { seller: soldByText(t, seller) })}.
      </Typography>
      {operatedBy ? (
        <Typography sx={smallSx}>{t('sellerDisclosures.operatedBy', { partner: operatedBy })}</Typography>
      ) : null}
      {fundsLine ? <Typography sx={smallSx}>{fundsLine}</Typography> : null}
      <Typography sx={smallSx}>{t('sellerDisclosures.refund')}</Typography>
      <Typography sx={smallSx}>{tcrfLine}</Typography>
    </Box>
  );
}

// Map a public-package API payload (partner_* fields) to the seller shape.
export function sellerFromPackage(pkg) {
  if (!pkg) return null;
  return {
    name: pkg.partner_name || '',
    address: pkg.partner_address || '',
    phone: pkg.partner_phone || '',
    referenceNumber: pkg.partner_seller_reference_number || null,
    referenceKind: pkg.partner_seller_reference_kind || (pkg.partner_country === 'US' ? 'cst' : 'license'),
    referenceRegion: pkg.partner_seller_reference_region || null,
    fundsProtection: pkg.partner_seller_funds_protection || null,
    tcrfParticipant: Boolean(pkg.partner_seller_tcrf_participant),
  };
}

export default SellerDisclosureBox;
